import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { IconButton } from '@mui/material';
import clsx from 'clsx';
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { ReactElement, useCallback, useEffect, useState } from 'react';

interface CarouselProps<T extends { id: number }> {
    slides: T[];
    options?: EmblaOptionsType;
    current: number;
    onSlideChange?: (newIndex: number) => void;
    slidesPerView?: number;
    spacing?: number;
    enableFade?: boolean;
    renderSlide: (slide: T) => ReactElement;
}

const EmblaCarousel = <T extends { id: number }>(props: CarouselProps<T>) => {
    const { slides, options, current, slidesPerView = 8, spacing, enableFade = true, renderSlide } = props;
    const [showFade, setShowFade] = useState(false);
    const [emblaRef, emblaApi] = useEmblaCarousel(options);

    const handleNext = () => {
        emblaApi?.scrollTo(current + 1);
        props.onSlideChange?.(current + 1);
    };

    const handlePrev = () => {
        emblaApi?.scrollTo(current - 1);
        props.onSlideChange?.(current - 1);
    };

    const handleClick = (newIndex: number) => {
        if (!emblaApi?.slidesInView().includes(newIndex)) {
            emblaApi?.scrollTo(newIndex);
        }
        props.onSlideChange?.(newIndex);
    };

    const onScrollChange = useCallback((emblaApi: EmblaCarouselType) => {
        const slidesInView = emblaApi.slidesInView();
        const lastIndex = emblaApi.slideNodes().length - 1;
        setShowFade(!slidesInView.includes(lastIndex));
    }, []);

    useEffect(() => {
        if (emblaApi && enableFade) {
            emblaApi.on('scroll', onScrollChange);
            emblaApi.on('slidesInView', onScrollChange);
            onScrollChange(emblaApi);

            return () => {
                emblaApi?.off('scroll', onScrollChange);
                emblaApi?.off('slidesInView', onScrollChange);
            };
        }
    }, [emblaApi, enableFade, onScrollChange]);

    return (
        <section className="flex items-center">
            <IconButton onClick={handlePrev} disabled={current <= 0} className="h-min" color="black">
                <ArrowBackIosNewIcon />
            </IconButton>
            <div className="relative overflow-hidden mx-[20px]" ref={emblaRef}>
                <div
                    className={clsx('flex touch-pan-y touch-pinch-zoom mx-[5px]', {
                        'justify-center': slides.length <= slidesPerView,
                    })}
                    style={{
                        gap: spacing,
                    }}
                >
                    {slides.map((slide, index) => (
                        <div
                            className={clsx('min-w-[50px] h-[50px] flex justify-center cursor-pointer', {
                                'border-2 border-primary-600 rounded': current === index,
                            })}
                            key={index}
                            onClick={() => handleClick(index)}
                            style={{
                                flexGrow: 0,
                                flexShrink: 0,
                                flexBasis: `${100 / slidesPerView}%`,
                            }}
                        >
                            {renderSlide(slide)}
                        </div>
                    ))}
                </div>
                <div
                    className={clsx('absolute top-0 right-0 h-full w-[5%] bg-gradient-to-r from-transparent to-white', {
                        hidden: !showFade || !enableFade,
                    })}
                />
            </div>
            <div className="embla__controls">
                <IconButton
                    onClick={handleNext}
                    disabled={current >= slides.length - 1}
                    className="h-min"
                    color="black"
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </div>
        </section>
    );
};

export default EmblaCarousel;
